import React, { useState, useEffect } from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { useAuth } from '../../../context/AuthContext';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { psAPI } from '../../../services/api';
import { toast } from 'sonner';
import {
  Package,
  Plus,
  Search,
  RefreshCw,
  ArrowRightCircle,
  Calendar,
  Eye,
  Pencil
} from 'lucide-react';

export default function Materials() {
  const [reservations, setReservations] = useState([]);
  const { canWrite } = useAuth();
  const [goodsIssues, setGoodsIssues] = useState([]);
  const [activities, setActivities] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateReservationDialog, setShowCreateReservationDialog] = useState(false);
  const [showGoodsIssueDialog, setShowGoodsIssueDialog] = useState(false);
  const [showViewEditDialog, setShowViewEditDialog] = useState(false);
  const [showViewGoodsIssueDialog, setShowViewGoodsIssueDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [selectedGoodsIssue, setSelectedGoodsIssue] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('__all__');
  const [activeTab, setActiveTab] = useState('reservations');

  const [reservationForm, setReservationForm] = useState({
    activity_id: '',
    material_id: '',
    material_description: '',
    required_quantity: 0,
    unit: 'EA',
    requirement_date: '',
    storage_location_id: '',
    plant_id: ''
  });

  const [editReservationForm, setEditReservationForm] = useState({
    material_description: '',
    required_quantity: 0,
    unit: 'EA',
    requirement_date: '',
    storage_location_id: '',
    plant_id: ''
  });

  const [goodsIssueForm, setGoodsIssueForm] = useState({
    reservation_id: '',
    issue_quantity: 0,
    posting_date: new Date().toISOString().split('T')[0],
    storage_location_id: ''
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [reservationsRes, goodsIssuesRes, activitiesRes, networksRes] = await Promise.all([
        psAPI.getMaterialReservations(),
        psAPI.getGoodsIssues(),
        psAPI.getActivities(),
        psAPI.getNetworks()
      ]);
      setReservations(reservationsRes.data);
      setGoodsIssues(goodsIssuesRes.data);
      setActivities(activitiesRes.data);
      setNetworks(networksRes.data);
    } catch (error) {
      toast.error('Failed to load materials data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateReservation = async () => {
    try {
      const payload = {
        ...reservationForm,
        required_quantity: parseFloat(reservationForm.required_quantity) || 0
      };
      if (!payload.storage_location_id) delete payload.storage_location_id;
      if (!payload.plant_id) delete payload.plant_id;
      
      await psAPI.createMaterialReservation(payload);
      toast.success('Material reservation created');
      setShowCreateReservationDialog(false);
      setReservationForm({
        activity_id: '',
        material_id: '',
        material_description: '',
        required_quantity: 0,
        unit: 'EA',
        requirement_date: '',
        storage_location_id: '',
        plant_id: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create reservation');
    }
  };

  const handleGoodsIssue = async () => {
    try {
      const payload = {
        ...goodsIssueForm,
        issue_quantity: parseFloat(goodsIssueForm.issue_quantity) || 0
      };
      
      await psAPI.createGoodsIssue(payload);
      toast.success('Goods issue posted');
      setShowGoodsIssueDialog(false);
      setSelectedReservation(null);
      setGoodsIssueForm({
        reservation_id: '',
        issue_quantity: 0,
        posting_date: new Date().toISOString().split('T')[0],
        storage_location_id: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post goods issue');
    }
  };

  const openGoodsIssueDialog = (reservation) => {
    setSelectedReservation(reservation);
    setGoodsIssueForm({
      reservation_id: reservation.reservation_id,
      issue_quantity: reservation.open_quantity || reservation.required_quantity,
      posting_date: new Date().toISOString().split('T')[0],
      storage_location_id: reservation.storage_location_id || ''
    });
    setShowGoodsIssueDialog(true);
  };

  const handleViewReservation = async (reservation) => {
    try {
      const response = await psAPI.getMaterialReservation(reservation.reservation_id);
      setSelectedReservation(response.data);
      setEditReservationForm({
        material_description: response.data.material_description || '',
        required_quantity: response.data.required_quantity || 0,
        unit: response.data.unit || 'EA',
        requirement_date: response.data.requirement_date || '',
        storage_location_id: response.data.storage_location_id || '',
        plant_id: response.data.plant_id || ''
      });
      setIsEditMode(false);
      setShowViewEditDialog(true);
    } catch (error) {
      toast.error('Failed to load reservation details');
    }
  };

  const handleEditReservation = () => {
    setIsEditMode(true);
  };

  const handleSaveReservationEdit = async () => {
    try {
      const payload = {
        material_description: editReservationForm.material_description || undefined,
        required_quantity: parseFloat(editReservationForm.required_quantity) || undefined,
        unit: editReservationForm.unit || undefined,
        requirement_date: editReservationForm.requirement_date || undefined,
        storage_location_id: editReservationForm.storage_location_id || undefined,
        plant_id: editReservationForm.plant_id || undefined
      };
      
      await psAPI.updateMaterialReservation(selectedReservation.reservation_id, payload);
      toast.success('Reservation updated successfully');
      setShowViewEditDialog(false);
      setSelectedReservation(null);
      setIsEditMode(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update reservation');
    }
  };

  const handleViewGoodsIssue = async (gi) => {
    try {
      const response = await psAPI.getGoodsIssue(gi.goods_issue_id);
      setSelectedGoodsIssue(response.data);
      setShowViewGoodsIssueDialog(true);
    } catch (error) {
      toast.error('Failed to load goods issue details');
    }
  };

  const filteredReservations = reservations.filter(r => {
    const matchesSearch = !searchQuery || 
      r.reservation_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.material_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.material_description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === '__all__' || r.reservation_status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const variants = {
      'OPEN': 'bg-orange-100 text-orange-700',
      'PARTIALLY_ISSUED': 'bg-amber-100 text-amber-700',
      'FULLY_ISSUED': 'bg-emerald-100 text-emerald-700',
      'CANCELLED': 'bg-red-100 text-red-700'
    };
    return <Badge className={variants[status] || 'bg-gray-100'}>{status?.replace('_', ' ')}</Badge>;
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Material Management</h1>
            <p className="text-sm text-gray-500 mt-1">Material reservations and goods issues for projects</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={fetchData} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            {canWrite('project_system') && (
            <Button onClick={() => setShowCreateReservationDialog(true)} data-testid="create-reservation-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Reservation
            </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="reservations" data-testid="tab-reservations">
              Reservations ({reservations.length})
            </TabsTrigger>
            <TabsTrigger value="goods-issues" data-testid="tab-goods-issues">
              Goods Issues ({goodsIssues.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="reservations" className="space-y-4">
            {/* Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Search reservations..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                        data-testid="search-reservations"
                      />
                    </div>
                  </div>
                  <div className="w-40">
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger>
                        <SelectValue placeholder="All Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">All Status</SelectItem>
                        <SelectItem value="OPEN">Open</SelectItem>
                        <SelectItem value="PARTIALLY_ISSUED">Partially Issued</SelectItem>
                        <SelectItem value="FULLY_ISSUED">Fully Issued</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Reservations List */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Material Reservations
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center h-32">
                    <RefreshCw className="w-6 h-6 animate-spin text-orange-600" />
                  </div>
                ) : filteredReservations.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No reservations found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left p-3">Reservation ID</th>
                          <th className="text-left p-3">Activity</th>
                          <th className="text-left p-3">Material</th>
                          <th className="text-right p-3">Required</th>
                          <th className="text-right p-3">Issued</th>
                          <th className="text-right p-3">Open</th>
                          <th className="text-left p-3">Status</th>
                          <th className="text-left p-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredReservations.map((res) => (
                          <tr key={res.reservation_id} className="border-b hover:bg-gray-50">
                            <td className="p-3 font-medium text-orange-600">{res.reservation_id}</td>
                            <td className="p-3">{res.activity_id}</td>
                            <td className="p-3">
                              <div>
                                <p className="font-medium">{res.material_id}</p>
                                <p className="text-xs text-gray-500">{res.material_description}</p>
                              </div>
                            </td>
                            <td className="p-3 text-right">{res.required_quantity} {res.unit}</td>
                            <td className="p-3 text-right">{res.issued_quantity || 0} {res.unit}</td>
                            <td className="p-3 text-right">{res.open_quantity || res.required_quantity} {res.unit}</td>
                            <td className="p-3">{getStatusBadge(res.reservation_status)}</td>
                            <td className="p-3">
                              <div className="flex gap-1">
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => handleViewReservation(res)}
                                  data-testid={`view-${res.reservation_id}`}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {res.reservation_status !== 'FULLY_ISSUED' && res.reservation_status !== 'CANCELLED' && (
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => openGoodsIssueDialog(res)}
                                    data-testid={`issue-${res.reservation_id}`}
                                  >
                                    <ArrowRightCircle className="w-3 h-3 mr-1" />
                                    Issue
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="goods-issues">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ArrowRightCircle className="w-5 h-5" />
                  Goods Issues
                </CardTitle>
              </CardHeader>
              <CardContent>
                {goodsIssues.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <ArrowRightCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No goods issues found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left p-3">Issue ID</th>
                          <th className="text-left p-3">Reservation</th>
                          <th className="text-left p-3">Material</th>
                          <th className="text-right p-3">Quantity</th>
                          <th className="text-left p-3">Posting Date</th>
                          <th className="text-right p-3">Value</th>
                          <th className="text-left p-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {goodsIssues.map((gi) => (
                          <tr key={gi.goods_issue_id} className="border-b hover:bg-gray-50">
                            <td className="p-3 font-medium text-orange-600">{gi.goods_issue_id}</td>
                            <td className="p-3">{gi.reservation_id}</td>
                            <td className="p-3">{gi.material_id}</td>
                            <td className="p-3 text-right">{gi.issue_quantity} {gi.unit}</td>
                            <td className="p-3">
                              <div className="flex items-center gap-1">
                                <Calendar className="w-4 h-4 text-gray-400" />
                                {gi.posting_date}
                              </div>
                            </td>
                            <td className="p-3 text-right">${(gi.total_value || 0).toLocaleString()}</td>
                            <td className="p-3">
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => handleViewGoodsIssue(gi)}
                                data-testid={`view-${gi.goods_issue_id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Reservation Dialog */}
      <Dialog open={showCreateReservationDialog} onOpenChange={setShowCreateReservationDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Material Reservation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Activity *</Label>
              <Select value={reservationForm.activity_id} onValueChange={(v) => setReservationForm({ ...reservationForm, activity_id: v })}>
                <SelectTrigger data-testid="select-activity">
                  <SelectValue placeholder="Select activity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Select activity</SelectItem>
                  {activities.map(a => (
                    <SelectItem key={a.activity_id} value={a.activity_id}>
                      {a.activity_id} - {a.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Material ID *</Label>
                <Input
                  value={reservationForm.material_id}
                  onChange={(e) => setReservationForm({ ...reservationForm, material_id: e.target.value })}
                  placeholder="e.g., MAT-001"
                  data-testid="input-material-id"
                />
              </div>
              <div className="space-y-2">
                <Label>Unit</Label>
                <Select value={reservationForm.unit} onValueChange={(v) => setReservationForm({ ...reservationForm, unit: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EA">EA (Each)</SelectItem>
                    <SelectItem value="KG">KG (Kilogram)</SelectItem>
                    <SelectItem value="L">L (Liter)</SelectItem>
                    <SelectItem value="M">M (Meter)</SelectItem>
                    <SelectItem value="PC">PC (Piece)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Material Description</Label>
              <Input
                value={reservationForm.material_description}
                onChange={(e) => setReservationForm({ ...reservationForm, material_description: e.target.value })}
                placeholder="Material description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Required Quantity *</Label>
                <Input
                  type="number"
                  value={reservationForm.required_quantity}
                  onChange={(e) => setReservationForm({ ...reservationForm, required_quantity: e.target.value })}
                  min="0"
                  data-testid="input-quantity"
                />
              </div>
              <div className="space-y-2">
                <Label>Requirement Date</Label>
                <Input
                  type="date"
                  value={reservationForm.requirement_date}
                  onChange={(e) => setReservationForm({ ...reservationForm, requirement_date: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Plant ID</Label>
                <Input
                  value={reservationForm.plant_id}
                  onChange={(e) => setReservationForm({ ...reservationForm, plant_id: e.target.value })}
                  placeholder="e.g., P001"
                />
              </div>
              <div className="space-y-2">
                <Label>Storage Location</Label>
                <Input
                  value={reservationForm.storage_location_id}
                  onChange={(e) => setReservationForm({ ...reservationForm, storage_location_id: e.target.value })}
                  placeholder="e.g., SL01"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateReservationDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateReservation}
              disabled={!reservationForm.activity_id || reservationForm.activity_id === '__none__' || !reservationForm.material_id || !reservationForm.required_quantity}
              data-testid="submit-reservation"
            >
              Create Reservation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Goods Issue Dialog */}
      <Dialog open={showGoodsIssueDialog} onOpenChange={setShowGoodsIssueDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Post Goods Issue</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedReservation && (
              <div className="bg-gray-50 p-3 rounded-lg text-sm">
                <p><strong>Reservation:</strong> {selectedReservation.reservation_id}</p>
                <p><strong>Material:</strong> {selectedReservation.material_id} - {selectedReservation.material_description}</p>
                <p><strong>Open Qty:</strong> {selectedReservation.open_quantity || selectedReservation.required_quantity} {selectedReservation.unit}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Issue Quantity *</Label>
                <Input
                  type="number"
                  value={goodsIssueForm.issue_quantity}
                  onChange={(e) => setGoodsIssueForm({ ...goodsIssueForm, issue_quantity: e.target.value })}
                  min="0"
                  max={selectedReservation?.open_quantity || selectedReservation?.required_quantity}
                  data-testid="input-issue-quantity"
                />
              </div>
              <div className="space-y-2">
                <Label>Posting Date</Label>
                <Input
                  type="date"
                  value={goodsIssueForm.posting_date}
                  onChange={(e) => setGoodsIssueForm({ ...goodsIssueForm, posting_date: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Storage Location</Label>
              <Input
                value={goodsIssueForm.storage_location_id}
                onChange={(e) => setGoodsIssueForm({ ...goodsIssueForm, storage_location_id: e.target.value })}
                placeholder="Storage location"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGoodsIssueDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleGoodsIssue}
              disabled={!goodsIssueForm.issue_quantity || goodsIssueForm.issue_quantity <= 0}
              data-testid="submit-goods-issue"
            >
              Post Goods Issue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View/Edit Reservation Dialog */}
      <Dialog open={showViewEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowViewEditDialog(false);
          setSelectedReservation(null);
          setIsEditMode(false);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{isEditMode ? 'Edit Reservation' : 'Reservation Details'}</span>
              {!isEditMode && selectedReservation?.reservation_status === 'OPEN' && (
                <Button size="sm" variant="outline" onClick={handleEditReservation} data-testid="edit-reservation-btn">
                  <Pencil className="w-4 h-4 mr-1" />
                  Edit
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedReservation && (
            <div className="space-y-4 py-4">
              {/* Header Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-xs text-gray-500">Reservation ID</Label>
                  <div className="font-medium">{selectedReservation.reservation_id}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Status</Label>
                  <div>{getStatusBadge(selectedReservation.reservation_status)}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Activity</Label>
                  <div className="font-medium">{selectedReservation.activity_id}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Material ID</Label>
                  <div className="font-medium">{selectedReservation.material_id}</div>
                </div>
              </div>

              {/* Editable Fields */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Material Description</Label>
                  {isEditMode ? (
                    <Input
                      value={editReservationForm.material_description}
                      onChange={(e) => setEditReservationForm({ ...editReservationForm, material_description: e.target.value })}
                      data-testid="edit-description"
                    />
                  ) : (
                    <p className="p-2 bg-gray-50 rounded">{selectedReservation.material_description || '-'}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Required Quantity</Label>
                    {isEditMode ? (
                      <Input
                        type="number"
                        value={editReservationForm.required_quantity}
                        onChange={(e) => setEditReservationForm({ ...editReservationForm, required_quantity: e.target.value })}
                        min="0"
                        data-testid="edit-quantity"
                      />
                    ) : (
                      <p className="p-2 bg-gray-50 rounded">{selectedReservation.required_quantity} {selectedReservation.unit}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Requirement Date</Label>
                    {isEditMode ? (
                      <Input
                        type="date"
                        value={editReservationForm.requirement_date}
                        onChange={(e) => setEditReservationForm({ ...editReservationForm, requirement_date: e.target.value })}
                        data-testid="edit-date"
                      />
                    ) : (
                      <p className="p-2 bg-gray-50 rounded">{selectedReservation.requirement_date || '-'}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Plant ID</Label>
                    {isEditMode ? (
                      <Input
                        value={editReservationForm.plant_id}
                        onChange={(e) => setEditReservationForm({ ...editReservationForm, plant_id: e.target.value })}
                      />
                    ) : (
                      <p className="p-2 bg-gray-50 rounded">{selectedReservation.plant_id || '-'}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Storage Location</Label>
                    {isEditMode ? (
                      <Input
                        value={editReservationForm.storage_location_id}
                        onChange={(e) => setEditReservationForm({ ...editReservationForm, storage_location_id: e.target.value })}
                      />
                    ) : (
                      <p className="p-2 bg-gray-50 rounded">{selectedReservation.storage_location_id || '-'}</p>
                    )}
                  </div>
                </div>

                {/* Read-only info */}
                <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Issued Quantity</Label>
                    <p className="font-medium">{selectedReservation.issued_quantity || 0} {selectedReservation.unit}</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Open Quantity</Label>
                    <p className="font-medium">{selectedReservation.open_quantity || selectedReservation.required_quantity} {selectedReservation.unit}</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Created At</Label>
                    <p className="text-sm">{selectedReservation.created_at ? new Date(selectedReservation.created_at).toLocaleDateString() : '-'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            {isEditMode ? (
              <>
                <Button variant="outline" onClick={() => setIsEditMode(false)}>Cancel</Button>
                <Button onClick={handleSaveReservationEdit} data-testid="save-reservation-btn">Save Changes</Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => setShowViewEditDialog(false)}>Close</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Goods Issue Dialog */}
      <Dialog open={showViewGoodsIssueDialog} onOpenChange={setShowViewGoodsIssueDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Goods Issue Details</DialogTitle>
          </DialogHeader>
          {selectedGoodsIssue && (
            <div className="space-y-4 py-4">
              {/* Header Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-xs text-gray-500">Goods Issue ID</Label>
                  <p className="font-medium">{selectedGoodsIssue.goods_issue_id}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Reservation</Label>
                  <p className="font-medium">{selectedGoodsIssue.reservation_id}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Material</Label>
                  <p className="font-medium">{selectedGoodsIssue.material_id}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Posting Date</Label>
                  <p className="font-medium">{selectedGoodsIssue.posting_date}</p>
                </div>
              </div>

              {/* Details */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Issue Quantity</Label>
                    <p className="p-2 bg-gray-50 rounded font-medium">{selectedGoodsIssue.issue_quantity} {selectedGoodsIssue.unit}</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Total Value</Label>
                    <p className="p-2 bg-gray-50 rounded font-medium">${(selectedGoodsIssue.total_value || 0).toLocaleString()}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Storage Location</Label>
                    <p className="p-2 bg-gray-50 rounded">{selectedGoodsIssue.storage_location_id || '-'}</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Movement Type</Label>
                    <p className="p-2 bg-gray-50 rounded">{selectedGoodsIssue.movement_type || '-'}</p>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Created At</Label>
                    <p className="text-sm">{selectedGoodsIssue.created_at ? new Date(selectedGoodsIssue.created_at).toLocaleString() : '-'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewGoodsIssueDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
